import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DialogTitle from "@mui/material/DialogTitle";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import DOMPurify from "dompurify";

import KMKDateRangePicker from "../../../Common/KMKDateRangePicker";
import TableWithPaginationSorting from "../../../Common/Table/TableWithPaginationSorting";
import { KMKDialog } from "../../../Common";
import ErrorRecords from "./ErrorRecords";
import FileHistory from "./FileHistory";

import { Job_Status_Types, JobHistoryColumns, Detailed_Columns, rowsPerPageOptions } from "../utils/JobData";
import { convertTimezone } from "../../../../util/common";
import { dayjs } from "../../../../util/dayjs";
import { regexName } from "../../../../util/regularExpress";
import { showToast } from "../../../../store/actions";
import WorkspaceService from "../../../../service/workspace";

const useStyles = makeStyles()(() => ({
  dialogPaper: {
    "& .MuiDialog-paper": {
      maxWidth: "960px",
      width: "960px",
    },
  },
  contentContainer: {
    width: "100%",
    textAlign: "left",
    minHeight: "100px",
    padding: "12px 24px 24px",
    borderRadius: "24px",
    backgroundColor: "#FFFFFF",
    "& .MuiTextField-root": {
      margin: 0,
    },
    "& .MuiAlert-icon": {
      padding: 0,
      backgroundColor: "#67A7D5",
      borderTopLeftRadius: "10px",
      borderBottomLeftRadius: "10px",
      marginLeft: "-1px",
      height: "40px",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        height: "100%",
        color: "#FFFFFF",
      },
    },
    "& .MuiAlert-message": {
      padding: "5px",
      color: "#A2A2A2",
    },
  },
}));

const JOB_WITH_DETAILS = [1, 3, 4, 5, 6];

const formatDate = (date, displayFormat) =>
  displayFormat ? date.format("MM/DD/YYYY HH:mm:ss") : date.format("YYYY-MM-DD HH:mm:ss");

export default function JobHistoryPopup({ handleCancel, popupTitle, jobId, jobType, fromPipeline = false }) {
  const { Workspace_id } = useParams();

  const workspace = useSelector((state) => state.workspace);
  const { IsDataVaultEnabled } = workspace.workspace;

  const user = useSelector((state) => state.user);
  const { UserTimezone } = useMemo(() => user?.currentUser || {}, [user]);

  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [needDetails, setNeedDetails] = useState(false);
  const [openFileHistory, setOpenFileHistory] = useState(false);
  const [openErrorRecords, setOpenErrorRecords] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [hideErrRecords, setHideErrRecords] = useState(false);
  const [blockDetailedHistory, setBlockDetailedHistory] = useState(false);
  const [detailedHistory, setDetailedHistory] = useState([]);
  const [errorRecords, setErrorRecords] = useState({});

  const [jobHistory_id, setJobHistory_id] = useState(-1);
  const [dateRange, setDateRange] = useState(null);
  const [displayDate, setDisplayDate] = useState(null);
  //variables useful for pagination and sorting
  const [search, setSearch] = useState("");
  const [count, setCount] = useState(0);
  const [detailCount, setDetailCount] = useState(0);
  const [options, setOptions] = useState({
    orderBy: "StartTime",
    order: "desc",
  });
  const [detailOptions, setDetailOptions] = useState({
    limit: 50,
    page: 0,
    orderBy: "LOAD_DATE",
    order: "desc",
    search: "",
  });

  const optionsRef = useRef(options);
  const detailedOptionsRef = useRef(detailOptions);
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const disabledDate = useCallback((current) => current && current > dayjs().endOf("day"), []);

  const disableDetailedDatePicker = useCallback(
    (current) => {
      if (displayDate) {
        return (
          current &&
          (current < dayjs(displayDate[0]).startOf("day") ||
            (displayDate[1] ? current > dayjs(displayDate[1]).endOf("day") : false))
        );
      }
      return false;
    },
    [displayDate]
  );

  const handleBackClick = () => {
    setSearch("");
    setDateRange(null);
    setDetailOptions({ limit: 50, page: 0, orderBy: "LOAD_DATE", order: "desc", search: "" });

    if (openFileHistory) setOpenFileHistory(false);
    else if (openErrorRecords) {
      setOpenFileHistory(true);
      setOpenErrorRecords(false);
    } else if (fromPipeline) handleCancel();

    setNeedDetails(false);
  };

  // Get History List
  const getHistoryList = useCallback(
    async (dates, tableOptions = optionsRef.current) => {
      setLoading(true);

      const wsService = new WorkspaceService();
      const result = await wsService.getJobHistory(
        Workspace_id,
        jobId,
        dates && dates[0] ? formatDate(dates[0]) : undefined,
        dates && dates[1] ? formatDate(dates[1]) : undefined,
        tableOptions,
        UserTimezone
      );

      if (result.success) {
        const { historyData } = result.data;
        let historyDataArr = [];

        historyData.length &&
          historyData.forEach((row) => {
            historyDataArr.push([
              {
                JobHistory_id: row.JobHistory_id,
                value: row.StartTime ? convertTimezone(row.StartTime) : "-",
                align: "left",
              },
              {
                JobHistory_id: row.JobHistory_id,
                value: row.EndTime ? convertTimezone(row.EndTime) : "-",
                align: "left",
              },
              {
                JobHistory_id: row.JobHistory_id,
                value: Job_Status_Types[row.Status?.toLowerCase()]?.statusText ?? "-",
                align: "left",
                color: Job_Status_Types[row.Status?.toLowerCase()]?.color,
              },
              {
                JobHistory_id: row.JobHistory_id,
                message: jobType === 2 ? row?.Result?.message : row?.message || "",
                value:
                  jobType === 2 || jobType === 1 ? (
                    <>
                      {row.Result?.failureMessage && (
                        <Tooltip
                          arrow
                          title={
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.Result?.failureMessage) }} />
                          }
                        >
                          <IconButton
                            size="small"
                            color={["failed", "error"].includes(row.Status?.toLowerCase()) ? "error" : "info"}
                            sx={{ mr: "5px", padding: "5px 0", cursor: "default" }}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.Result?.fileHistory?.length ? (
                        <Tooltip arrow title="View Files">
                          <IconButton
                            size="small"
                            color={["failed", "error"].includes(row.Status?.toLowerCase()) ? "error" : "info"}
                            sx={{ mr: "5px", padding: "5px 0" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setHideErrRecords(row.Result?.hideErrRecords);
                              setFileList(row.Result?.fileHistory);
                              setJobHistory_id(row.JobHistory_id);
                              setOpenFileHistory(true);
                            }}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <></>
                      )}
                      {row?.Result?.message || "-"}
                    </>
                  ) : (
                    row?.message || "-"
                  ),
                align: "left",
                color:
                  row.Status?.toLowerCase() === "failed" || row.Status?.toLowerCase() === "error"
                    ? Job_Status_Types[row.Status?.toLowerCase()]?.color
                    : "black",
              },
            ]);
          });
        setHistoryList(historyDataArr);
      }
      setDataLoaded(true);
      setLoading(false);
    },
    [UserTimezone, Workspace_id, jobId, jobType]
  );

  // Get Detailed History List
  const getDetailedHistoryList = useCallback(
    async (dates, tableOptions = detailedOptionsRef.current, jobHistory_id) => {
      setLoading(true);

      const wsService = new WorkspaceService();
      const result = await wsService.getDetailedJobHistory(
        Workspace_id,
        jobId,
        dates && dates[0] ? formatDate(dates[0]) : undefined,
        dates && dates[1] ? formatDate(dates[1]) : undefined,
        { ...tableOptions, UserTimezone },
        jobHistory_id
      );
      if (result.success) {
        const { historyData, count } = result.data;
        let historyDataArr = [];

        historyData.forEach((row) => {
          historyDataArr.push([
            ...(jobType !== 6
              ? [
                  {
                    value: row.STORED_PROCEDURE,
                    align: "left",
                  },
                ]
              : []),
            { value: row.STEP, align: "left" },
            {
              value: row.RESULT,
              align: "left",
              color:
                row.Status?.toLowerCase() === "failed" || row.Status?.toLowerCase() === "error"
                  ? Job_Status_Types[row.Status?.toLowerCase()]?.color
                  : "black",
              message: row.RESULT,
            },
            { value: row.Status, align: "left", color: Job_Status_Types[row.Status?.toLowerCase()]?.color },
            {
              value: row.LOAD_DATE ? convertTimezone(row.LOAD_DATE) : null,
              align: "left",
            },
          ]);
        });
        setDetailedHistory(historyDataArr);
        setDetailCount(count);
      } else setCount(0);

      setDataLoaded(true);
      setLoading(false);
    },
    [UserTimezone, Workspace_id, jobId, jobType]
  );

  // Get Detailed History List
  const getErroredRecordsList = useCallback(
    async (jobHistory_id, sourceId, fileLocation) => {
      setLoading(true);
      setOpenFileHistory(false);
      setOpenErrorRecords(true);

      const wsService = new WorkspaceService();
      const { data, success } = await wsService.getErroredRecordsList(
        Workspace_id,
        jobId,
        jobHistory_id,
        sourceId,
        fileLocation
      );
      if (success) setErrorRecords(data);

      setLoading(false);
    },
    [Workspace_id, jobId]
  );

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setDetailOptions({ ...detailOptions, search, page: 0 });
      getDetailedHistoryList(dateRange, { ...detailOptions, search, page: 0 }, jobHistory_id);
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setDetailOptions({ ...detailOptions, search: "", page: 0 });
    getDetailedHistoryList(dateRange, { ...detailOptions, search: "", page: 0 }, jobHistory_id);
  };

  const getDataUsingOptions = (tableOptions) => {
    getHistoryList(dateRange, { ...tableOptions, search });
  };

  const getDetailedDataUsingOptions = (tableOptions) => {
    getDetailedHistoryList(dateRange, { ...tableOptions, search }, jobHistory_id);
  };

  const getDetailedHistory = (row) => {
    let StartTime = row[0].value !== "-" ? new Date(row[0].value) : null;
    let EndTime = row[1].value !== "-" ? new Date(row[1].value) : null;
    setDisplayDate([dayjs(StartTime), dayjs(EndTime)]);
    if (!IsDataVaultEnabled) {
      setDateRange([dayjs(StartTime), dayjs(EndTime)]);
      getDetailedHistoryList([StartTime, EndTime], { ...detailOptions, search }, row[0]?.JobHistory_id);
    } else getDetailedHistoryList(dateRange, { ...detailOptions, search }, row[0]?.JobHistory_id);

    setJobHistory_id(row[0]?.JobHistory_id);
    setNeedDetails(true);
  };

  useEffect(() => {
    setBlockDetailedHistory(!JOB_WITH_DETAILS.includes(jobType));

    getHistoryList(null);

    return () => {
      setLoading(false);
      setHistoryList([]);
      setDataLoaded(false);
      setDateRange(null);
      setSearch("");
      setCount(0);
      setOptions({ limit: 10, page: 0, orderBy: "StartTime", order: "desc", search: "" });
      setDetailOptions({ limit: 50, page: 0, orderBy: "LOAD_DATE", order: "desc", search: "" });
    };
  }, [getHistoryList, jobType]);

  const title = useMemo(() => {
    if (openFileHistory) return "List of Files";
    if (openErrorRecords) return "List of First 50 Error Records";
    return `${needDetails ? "Detailed " : ""}${popupTitle}`;
  }, [needDetails, openErrorRecords, openFileHistory, popupTitle]);

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading && !dataLoaded}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog className={classes.dialogPaper} open={dataLoaded} onClose={handleCancel}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <DialogTitle sx={{ display: "flex", padding: "16px", marginBottom: "10px" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {needDetails || openFileHistory || openErrorRecords || fromPipeline ? (
              <ArrowBackIcon onClick={handleBackClick} sx={{ marginRight: "10px", cursor: "pointer" }} />
            ) : (
              <HistoryIcon sx={{ marginRight: "10px" }} />
            )}
            <span>
              {title}
              {needDetails &&
                displayDate &&
                !IsDataVaultEnabled &&
                `: "${formatDate(displayDate[0], true)}" - "${
                  displayDate[1] === null ? "Current" : formatDate(displayDate[1], true)
                }"`}
            </span>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              color: "black",
              marginLeft: "auto",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.contentContainer}>
          <Grid container columnSpacing={2} sx={{ maxWidth: "100%", overflowX: "hidden" }}>
            {needDetails && !openFileHistory && (
              <>
                <Grid size={4}>
                  <TextField
                    margin="normal"
                    value={search}
                    onKeyDown={handleSearch}
                    onChange={(e) => {
                      if (regexName(e.target.value, "customSpecialChar", " _:.-")) setSearch(e.target.value);
                      if (e.target.value === "") setDetailOptions({ ...detailOptions, search: e.target.value });
                    }}
                    placeholder="Search..."
                    variant="standard"
                    inputProps={{ maxLength: 50 }}
                    InputProps={{
                      startAdornment: <SearchIcon color="primary" />,
                      ...(search && {
                        endAdornment: (
                          <IconButton onClick={handleClearSearch} size="small">
                            <ClearIcon color="primary" />
                          </IconButton>
                        ),
                      }),
                    }}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  />
                </Grid>
                <Grid alignSelf="center" size={5}>
                  <KMKDateRangePicker
                    format={(value) => value.format("MM/DD/YYYY HH:mm:ss")}
                    value={dateRange}
                    defaultValue={displayDate}
                    onChange={(dates) => {
                      if (dates) {
                        if (
                          displayDate[0] > dates[0] ||
                          (displayDate[1] !== null ? displayDate[1] < dates[1] : false)
                        ) {
                          dispatch(
                            showToast({
                              message: "Datetime selection is not in the scope of this job run.",
                              type: "error",
                            })
                          );
                        } else {
                          setDetailOptions({ ...detailOptions, page: 0 });
                          setDateRange(dates);
                          getDetailedHistoryList(dates, { ...detailOptions, search, page: 0 }, jobHistory_id);
                        }
                      } else {
                        let tempDates = IsDataVaultEnabled ? dates : displayDate;
                        setDateRange(tempDates);
                        setDetailOptions({ ...detailOptions, page: 0 });
                        getDetailedHistoryList(tempDates, { ...detailOptions, search, page: 0 }, jobHistory_id);
                      }
                    }}
                    disabledDate={disableDetailedDatePicker}
                    size="middle"
                    separator="~"
                    allowClear={<ClearIcon sx={{ fontSize: "16px" }} />}
                    suffixIcon={
                      dateRange ? <ClearIcon sx={{ fontSize: "16px" }} /> : <DateRangeIcon sx={{ fontSize: "16px" }} />
                    }
                    variant="outlined"
                    showTime
                  />
                </Grid>
              </>
            )}
            {!needDetails && !openFileHistory && !openErrorRecords && (
              <Grid alignSelf="center" size={5}>
                <KMKDateRangePicker
                  format={(value) => value.format("MM/DD/YYYY HH:mm:ss")}
                  value={dateRange}
                  onChange={(value) => {
                    setOptions({ ...options, page: 0 });
                    setDateRange(value);
                    getHistoryList(value, { ...options, page: 0 }, false);
                  }}
                  disabledDate={disabledDate}
                  size="middle"
                  separator="~"
                  allowClear={{ clearIcon: <ClearIcon sx={{ fontSize: "16px" }} /> }}
                  suffixIcon={
                    dateRange ? <ClearIcon sx={{ fontSize: "16px" }} /> : <DateRangeIcon sx={{ fontSize: "16px" }} />
                  }
                  variant="outlined"
                  showTime
                />
              </Grid>
            )}
          </Grid>
          <Box sx={{ marginTop: "20px" }}>
            {!needDetails && !openFileHistory && !openErrorRecords && (
              <TableWithPaginationSorting
                columns={JobHistoryColumns}
                tableData={historyList}
                ellipsisColumns={[4]}
                tableMaxWidth="100%"
                tableMaxHeight="400px"
                noDataColspan={5}
                noDataMessage="No records found"
                handleRequest={getDataUsingOptions}
                rowsPerPageOptions={rowsPerPageOptions}
                defaultOrder="desc"
                defaultOrderBy="StartTime"
                options={options}
                setOptions={setOptions}
                count={count}
                {...(!blockDetailedHistory && { RowClick: getDetailedHistory })}
                rowHover={!blockDetailedHistory}
                usePagination={false}
                showCopy={true}
              />
            )}
            {needDetails && !openFileHistory && !blockDetailedHistory && (
              <TableWithPaginationSorting
                columns={Detailed_Columns.filter((col) => !(jobType === 6 && col.id === "STORED_PROCEDURE"))}
                tableData={detailedHistory}
                ellipsisColumns={jobType === 6 ? [2] : [3]}
                tableMaxWidth="100%"
                tableMaxHeight="400px"
                noDataColspan={jobType === 6 ? 4 : 5}
                noDataMessage="No records found"
                handleRequest={getDetailedDataUsingOptions}
                rowsPerPageOptions={rowsPerPageOptions}
                defaultOrder="desc"
                defaultOrderBy="LOAD_DATE"
                options={detailOptions}
                setOptions={setDetailOptions}
                count={detailCount}
                showCopy={rowsPerPageOptions}
              />
            )}
            {openFileHistory && (
              <FileHistory
                JobType={jobType}
                fileList={fileList}
                hideErrRecords={hideErrRecords}
                loadErroredData={getErroredRecordsList}
                jobHistory_id={jobHistory_id}
                jobId={jobId}
              />
            )}
            {openErrorRecords && <ErrorRecords data={errorRecords} />}
          </Box>
        </DialogContent>
      </KMKDialog>
    </>
  );
}
